import {
  Box,
  Button,
  Combobox,
  createListCollection,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Check, ChevronsUpDown } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  Checkbox,
  createTranslator,
  Field,
  Modal,
  type TranslateFn,
} from '@mangovoice/mango-ui'

import type { PbxUser, PbxUserCreate, PbxUserUpdate } from '../types/user.types'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PbxOption {
  id: number
  name: string
}

export interface UserGroupOption {
  id: number
  name: string
}

export interface PhoneNumberOption {
  id: number
  name: string
  number: string
}

interface FormState {
  pbx: string
  pin: string
  sending_fax_email: string
  sending_fax_number: string
  fax_title: string
  user_groups: number[]
}

export interface PbxUserFormDialogSubmitData {
  pbxId: number
  data: PbxUserCreate | PbxUserUpdate
}

export interface PbxUserFormDialogProps {
  isOpen: boolean
  onClose: () => void
  mode: 'add' | 'edit'
  userId: number
  pbxUser?: PbxUser | null
  pbxId?: number
  pbxOptions?: PbxOption[]
  userGroupOptions?: UserGroupOption[]
  /**
   * Phone numbers on the currently-selected PBX, used to populate the
   * "Sending Fax Number" autocomplete. The parent is responsible for
   * fetching these in response to `onPbxChange` callbacks.
   */
  phoneNumberOptions?: PhoneNumberOption[]
  isPhoneNumbersLoading?: boolean
  /** Fires when the selected PBX changes (add mode) or when the dialog opens. */
  onPbxChange?: (pbxId: number | null) => void
  isSubmitting: boolean
  onSubmit: (submitData: PbxUserFormDialogSubmitData) => void
  /**
   * When provided, the PBX combobox becomes a controlled, server-side search.
   * The consumer fetches matching PBXes and passes them via `pbxOptions`; the
   * dialog skips its internal client-side filtering.
   */
  onPbxSearchTermChange?: (term: string) => void
  /** Loading state for the controlled PBX search (only relevant when `onPbxSearchTermChange` is set). */
  isPbxSearchLoading?: boolean
  t?: TranslateFn
}

// ─── Component ────────────────────────────────────────────────────────────────

const EMAIL_REGEX = /^[^@\s]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/

export const PbxUserFormDialog: React.FC<PbxUserFormDialogProps> = ({
  isOpen,
  onClose,
  mode,
  userId,
  pbxUser,
  pbxId,
  pbxOptions = [],
  userGroupOptions = [],
  phoneNumberOptions = [],
  isPhoneNumbersLoading = false,
  onPbxChange,
  isSubmitting,
  onSubmit,
  onPbxSearchTermChange,
  isPbxSearchLoading = false,
  t,
}) => {
  const tr = useMemo(() => createTranslator(t), [t])
  const isAdd = mode === 'add'
  const isPbxSearchControlled = typeof onPbxSearchTermChange === 'function'
  const onPbxSearchTermChangeRef = useRef(onPbxSearchTermChange)
  useEffect(() => {
    onPbxSearchTermChangeRef.current = onPbxSearchTermChange
  }, [onPbxSearchTermChange])

  const [form, setForm] = useState<FormState>({
    pbx: pbxId?.toString() ?? '',
    pin: '',
    sending_fax_email: '',
    sending_fax_number: '',
    fax_title: '',
    user_groups: [],
  })
  const [pbxSearchTerm, setPbxSearchTerm] = useState('')
  const [faxNumberSearchTerm, setFaxNumberSearchTerm] = useState('')
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setHasAttemptedSubmit(false)
      if (isAdd) {
        setForm({
          pbx: '',
          pin: '',
          sending_fax_email: '',
          sending_fax_number: '',
          fax_title: '',
          user_groups: [],
        })
        setPbxSearchTerm('')
        setFaxNumberSearchTerm('')
        onPbxSearchTermChangeRef.current?.('')
      } else if (pbxUser) {
        setForm({
          pbx: pbxId?.toString() ?? '',
          pin: pbxUser.pin?.toString() ?? '',
          sending_fax_email: pbxUser.sending_fax_email || '',
          sending_fax_number: pbxUser.sending_fax_number || '',
          fax_title: pbxUser.fax_title || '',
          user_groups: pbxUser.user_groups || [],
        })
        setFaxNumberSearchTerm('')
      }
    }
  }, [isOpen, isAdd, pbxUser, pbxId])

  const selectedPbxId = useMemo(() => {
    if (!isOpen) return null
    if (form.pbx) {
      const parsed = parseInt(form.pbx, 10)
      return Number.isFinite(parsed) ? parsed : null
    }
    return pbxId ?? null
  }, [form.pbx, isOpen, pbxId])

  useEffect(() => {
    if (!isOpen) return
    onPbxChange?.(selectedPbxId)
  }, [isOpen, onPbxChange, selectedPbxId])

  const updateField = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      setForm((prev) => ({ ...prev, [field]: value }))
    },
    [],
  )

  const handleGroupToggle = useCallback((groupId: number) => {
    setForm((prev) => {
      const next = prev.user_groups.includes(groupId)
        ? prev.user_groups.filter((id) => id !== groupId)
        : [...prev.user_groups, groupId]
      return { ...prev, user_groups: next }
    })
  }, [])

  const normalizedSendingFaxEmail = form.sending_fax_email.trim()
  const isFaxEmailInvalid =
    normalizedSendingFaxEmail.length > 0 &&
    !EMAIL_REGEX.test(normalizedSendingFaxEmail)
  const shouldShowFaxEmailError = hasAttemptedSubmit && isFaxEmailInvalid

  const handleSubmit = useCallback(() => {
    setHasAttemptedSubmit(true)
    if (isFaxEmailInvalid) return

    const selectedPbxId = form.pbx ? parseInt(form.pbx, 10) : (pbxId ?? 0)
    if (isAdd) {
      const data: PbxUserCreate = {
        user: userId,
        pin: form.pin ? parseInt(form.pin, 10) : undefined,
        sending_fax_email: normalizedSendingFaxEmail || undefined,
        sending_fax_number: form.sending_fax_number || undefined,
        fax_title: form.fax_title || undefined,
        user_groups: form.user_groups.length > 0 ? form.user_groups : undefined,
      }
      onSubmit({ pbxId: selectedPbxId, data })
    } else {
      const data: PbxUserUpdate = {
        pin: form.pin ? parseInt(form.pin, 10) : null,
        sending_fax_email: normalizedSendingFaxEmail,
        sending_fax_number: form.sending_fax_number,
        fax_title: form.fax_title,
        user_groups: form.user_groups,
      }
      onSubmit({ pbxId: selectedPbxId, data })
    }
  }, [
    form,
    isAdd,
    isFaxEmailInvalid,
    normalizedSendingFaxEmail,
    userId,
    pbxId,
    onSubmit,
  ])

  const canSubmit = isAdd ? !!form.pbx : true
  const normalizedPbxSearchTerm = pbxSearchTerm.trim().toLowerCase()

  const filteredPbxOptions = useMemo(() => {
    // When the consumer drives the search, `pbxOptions` is already the
    // server-filtered set — don't filter again on the client.
    if (isPbxSearchControlled) return pbxOptions
    if (!normalizedPbxSearchTerm) return pbxOptions

    return pbxOptions.filter((option) =>
      option.name.toLowerCase().includes(normalizedPbxSearchTerm),
    )
  }, [pbxOptions, normalizedPbxSearchTerm, isPbxSearchControlled])

  const visiblePbxOptions = useMemo(() => {
    if (!form.pbx) return filteredPbxOptions

    const selectedPbxOption = pbxOptions.find(
      (option) => option.id.toString() === form.pbx,
    )
    if (!selectedPbxOption) return filteredPbxOptions

    const alreadyIncluded = filteredPbxOptions.some(
      (option) => option.id === selectedPbxOption.id,
    )
    if (alreadyIncluded) return filteredPbxOptions

    return [selectedPbxOption, ...filteredPbxOptions]
  }, [filteredPbxOptions, form.pbx, pbxOptions])

  const pbxCollection = useMemo(
    () =>
      createListCollection<PbxOption>({
        items: visiblePbxOptions,
        itemToValue: (item) => item.id.toString(),
        itemToString: (item) => item.name,
      }),
    [visiblePbxOptions],
  )

  const normalizedFaxNumberSearchTerm = useMemo(
    () => faxNumberSearchTerm.trim().toLowerCase(),
    [faxNumberSearchTerm],
  )

  const filteredFaxNumberOptions = useMemo(() => {
    if (!normalizedFaxNumberSearchTerm) return phoneNumberOptions
    return phoneNumberOptions.filter((option) => {
      const name = option.name.toLowerCase()
      const number = option.number.toLowerCase()
      return (
        name.includes(normalizedFaxNumberSearchTerm) ||
        number.includes(normalizedFaxNumberSearchTerm)
      )
    })
  }, [normalizedFaxNumberSearchTerm, phoneNumberOptions])

  const faxNumberCollection = useMemo(
    () =>
      createListCollection<PhoneNumberOption>({
        items: filteredFaxNumberOptions,
        itemToValue: (item) => item.number,
        itemToString: (item) =>
          item.name ? `${item.name} (${item.number})` : item.number,
      }),
    [filteredFaxNumberOptions],
  )

  const faxNumberInputValue = useMemo(() => {
    if (!form.sending_fax_number) return ''
    const match = phoneNumberOptions.find(
      (option) => option.number === form.sending_fax_number,
    )
    if (match)
      return match.name ? `${match.name} (${match.number})` : match.number
    return form.sending_fax_number
  }, [form.sending_fax_number, phoneNumberOptions])

  return (
    <Modal
      open={isOpen}
      onOpenChange={(d) => !d.open && onClose()}
      title={
        isAdd
          ? tr('users.pbxUserForm.titleAdd', 'Add to PBX')
          : tr('users.pbxUserForm.titleEdit', 'Edit PBX Association')
      }
      maxWidth="520px"
      closeOnEscape={!isSubmitting}
      footer={
        <Box display="flex" gap={3} justifyContent="flex-end">
          <Button
            variant="outline"
            colorPalette="neutral"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {tr('users.pbxUserForm.cancel', 'Cancel')}
          </Button>
          <Button
            colorPalette="brand"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit || isSubmitting}
          >
            {isAdd
              ? tr('users.pbxUserForm.submitAdd', 'Add to PBX')
              : tr('users.pbxUserForm.submitEdit', 'Save Changes')}
          </Button>
        </Box>
      }
    >
      <VStack align="stretch" gap={4}>
        {isAdd && (
          <Field.Root required>
            <Field.Label>{tr('users.pbxUserForm.pbxLabel', 'PBX')}</Field.Label>
            <Combobox.Root
              collection={pbxCollection}
              value={form.pbx ? [form.pbx] : []}
              onValueChange={(details) => {
                updateField('pbx', details.value[0] ?? '')
                updateField('sending_fax_number', '')
                setFaxNumberSearchTerm('')
                if (isPbxSearchControlled) {
                  onPbxSearchTermChange?.('')
                }
              }}
              onInputValueChange={(details) => {
                setPbxSearchTerm(details.inputValue)
                if (isPbxSearchControlled) {
                  onPbxSearchTermChange?.(details.inputValue)
                }
              }}
              inputBehavior="autohighlight"
              openOnClick
              width="100%"
              size="md"
            >
              <Combobox.Control>
                <Combobox.Input
                  placeholder={tr(
                    'users.pbxUserForm.pbxSearchPlaceholder',
                    'Search PBX by name...',
                  )}
                  bg="bg.subtle"
                  borderColor="border.default"
                  aria-label={tr(
                    'users.pbxUserForm.pbxSearchAria',
                    'Search PBX options by name',
                  )}
                />
                <Combobox.IndicatorGroup>
                  <Combobox.Trigger>
                    <ChevronsUpDown />
                  </Combobox.Trigger>
                </Combobox.IndicatorGroup>
              </Combobox.Control>
              <Combobox.Positioner>
                <Combobox.Content maxH="280px" overflowY="auto">
                  {isPbxSearchLoading ? (
                    <Combobox.Empty>
                      {tr('users.pbxUserForm.pbxSearching', 'Searching...')}
                    </Combobox.Empty>
                  ) : isPbxSearchControlled && !pbxSearchTerm.trim() ? (
                    <Combobox.Empty>
                      {tr(
                        'users.pbxUserForm.pbxTypeToSearch',
                        'Type to search PBX...',
                      )}
                    </Combobox.Empty>
                  ) : visiblePbxOptions.length === 0 ? (
                    <Combobox.Empty>
                      {tr(
                        'users.pbxUserForm.pbxNoMatches',
                        'No PBX matches that search.',
                      )}
                    </Combobox.Empty>
                  ) : (
                    visiblePbxOptions.map((option) => (
                      <Combobox.Item key={option.id} item={option}>
                        <Combobox.ItemText>{option.name}</Combobox.ItemText>
                        <Combobox.ItemIndicator>
                          <Check />
                        </Combobox.ItemIndicator>
                      </Combobox.Item>
                    ))
                  )}
                </Combobox.Content>
              </Combobox.Positioner>
            </Combobox.Root>
          </Field.Root>
        )}

        <Field.Root>
          <Field.Label>
            {tr('users.pbxUserForm.faxTitleLabel', 'Fax Title')}
          </Field.Label>
          <Input
            value={form.fax_title}
            onChange={(e) => updateField('fax_title', e.target.value)}
            placeholder={tr(
              'users.pbxUserForm.faxTitlePlaceholder',
              'Fax title',
            )}
            bg="bg.subtle"
            borderColor="border.default"
          />
        </Field.Root>

        <Field.Root invalid={shouldShowFaxEmailError}>
          <Field.Label>
            {tr('users.pbxUserForm.sendingFaxEmailLabel', 'Sending Fax Email')}
          </Field.Label>
          <Input
            value={form.sending_fax_email}
            onChange={(e) => updateField('sending_fax_email', e.target.value)}
            placeholder={tr(
              'users.pbxUserForm.sendingFaxEmailPlaceholder',
              'Fax email address',
            )}
            type="email"
            bg="bg.subtle"
            borderColor="border.default"
          />
          {shouldShowFaxEmailError && (
            <Field.ErrorText>
              {tr(
                'users.pbxUserForm.invalidEmail',
                'Enter a valid email address.',
              )}
            </Field.ErrorText>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>
            {tr(
              'users.pbxUserForm.sendingFaxNumberLabel',
              'Sending Fax Number',
            )}
          </Field.Label>
          <Combobox.Root
            collection={faxNumberCollection}
            value={form.sending_fax_number ? [form.sending_fax_number] : []}
            inputValue={faxNumberInputValue}
            onValueChange={(details) => {
              updateField('sending_fax_number', details.value[0] ?? '')
              setFaxNumberSearchTerm('')
            }}
            onInputValueChange={(details) => {
              setFaxNumberSearchTerm(details.inputValue)
            }}
            inputBehavior="autohighlight"
            openOnClick
            width="100%"
            size="md"
            disabled={!selectedPbxId}
          >
            <Combobox.Control>
              <Combobox.Input
                placeholder={
                  selectedPbxId
                    ? tr(
                        'users.pbxUserForm.faxNumberSearchPlaceholder',
                        'Search fax numbers...',
                      )
                    : tr(
                        'users.pbxUserForm.faxNumberSelectPbxFirst',
                        'Select a PBX first',
                      )
                }
                bg="bg.subtle"
                borderColor="border.default"
                aria-label={tr(
                  'users.pbxUserForm.faxNumberSearchAria',
                  'Search sending fax number options',
                )}
              />
              <Combobox.IndicatorGroup>
                <Combobox.Trigger>
                  <ChevronsUpDown />
                </Combobox.Trigger>
              </Combobox.IndicatorGroup>
            </Combobox.Control>
            <Combobox.Positioner>
              <Combobox.Content maxH="280px" overflowY="auto">
                <Combobox.Empty>
                  {isPhoneNumbersLoading
                    ? tr(
                        'users.pbxUserForm.faxNumbersLoading',
                        'Loading fax numbers...',
                      )
                    : tr(
                        'users.pbxUserForm.faxNumbersEmpty',
                        'No fax numbers found for this PBX.',
                      )}
                </Combobox.Empty>
                {filteredFaxNumberOptions.map((option) => (
                  <Combobox.Item key={option.id} item={option}>
                    <Combobox.ItemText>
                      {option.name
                        ? `${option.name} (${option.number})`
                        : option.number}
                    </Combobox.ItemText>
                    <Combobox.ItemIndicator>
                      <Check />
                    </Combobox.ItemIndicator>
                  </Combobox.Item>
                ))}
              </Combobox.Content>
            </Combobox.Positioner>
          </Combobox.Root>
        </Field.Root>

        <Field.Root>
          <Field.Label>{tr('users.pbxUserForm.pinLabel', 'PIN')}</Field.Label>
          <Input
            value={form.pin}
            onChange={(e) => updateField('pin', e.target.value)}
            placeholder={tr(
              'users.pbxUserForm.pinPlaceholder',
              '4-digit PIN (1000-9999)',
            )}
            type="number"
            bg="bg.subtle"
            borderColor="border.default"
          />
        </Field.Root>

        {/* NOTE: Intentionally NOT wrapped in Field.Root. Chakra v3's
                    Field.Root injects its single `ids.control` into every
                    child form control, so all nested checkbox inputs
                    share one id and the Field.Label's htmlFor routes every
                    click through to the first checkbox only. Use a plain Box
                    + Text heading here (same pattern as
                    EditPhonePermissionsDialog). */}
        {userGroupOptions.length > 0 && (
          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="fg.default" mb={1}>
              {tr('users.pbxUserForm.userGroupsLabel', 'User Groups')}
            </Text>
            <Box
              maxH="180px"
              overflowY="auto"
              borderWidth={1}
              borderColor="border.default"
              borderRadius="md"
              p={2}
            >
              <VStack align="stretch" gap={1}>
                {userGroupOptions.map((group) => (
                  <Checkbox
                    key={group.id}
                    checked={form.user_groups.includes(group.id)}
                    onCheckedChange={() => handleGroupToggle(group.id)}
                    size="sm"
                  >
                    {group.name}
                  </Checkbox>
                ))}
              </VStack>
            </Box>
            <Text fontSize="xs" color="fg.muted" mt={1}>
              {tr(
                'users.pbxUserForm.userGroupsSelected',
                `${form.user_groups.length} selected`,
                { count: form.user_groups.length },
              )}
            </Text>
          </Box>
        )}
      </VStack>
    </Modal>
  )
}
